/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

// Import the store and context
import {
  useSearchFilterStore,
  SearchFilterContextType,
  SearchFilterStore,
} from '../../modules/store/searchFilterZustandStore';
import { SearchFilterContext } from '../../organisms/SearchResults/index.context';

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 *
 * Uses feature flags: EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND and EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND
 *
 * Note: The Zustand store is updated proactively in searchResultsMainComponent where
 * the SearchFilterContext.Provider is set up, rather than reactively in this hook.
 * This is more efficient and follows the principle of updating at the source.
 */
export const useConditionalSearchFilterContext = (
  flagType: 'adf' | 'serp' = 'serp'
): SearchFilterContextType & Partial<Omit<SearchFilterStore, keyof SearchFilterContextType>> => {
  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Always call hooks at top level (Rules of Hooks)
  const contextValue = useContext(SearchFilterContext);

  // Subscribe to Zustand state only when the flag is on to avoid unnecessary re-renders.
  // When flag is off, pass a no-op selector that returns undefined (stable reference, no re-render).
  const searchQueryState = useSearchFilterStore(shouldUseZustand ? state => state.searchQueryState : () => undefined);
  const filterModalOpen = useSearchFilterStore(shouldUseZustand ? state => state.filterModalOpen : () => undefined);

  // Methods are stable references on a singleton store — use getState() to avoid
  // 12 individual Zustand subscriptions that add overhead for no reactivity benefit.
  // Extracted outside conditional to satisfy react-compiler (hooks cannot be referenced inside branches).
  const storeState = useSearchFilterStore.getState(); // eslint-disable-line react-compiler/react-compiler

  if (shouldUseZustand) {
    const {
      setSearchQuery,
      setFilterModalOpen,
      setQuery,
      setFilter,
      setApplyFilter,
      updateOtherSearchQueryParam,
      updatePoints,
      updateTaxesAndAvailability,
      updateCurrency,
      updateAmenities,
      updateActivities,
      updateBrands,
      updateTransportationTypes,
      updateQueryAndOtherSearchQueryParam,
    } = storeState;

    // Return Zustand store data with all direct methods
    return {
      searchQueryState: searchQueryState!, // Reactive state from selector (non-null when flag is on)
      setSearchQuery,
      filterModalOpen: filterModalOpen!, // Reactive state from selector (non-null when flag is on)
      setFilterModalOpen,
      setQuery,
      setFilter,
      setApplyFilter,
      updateOtherSearchQueryParam,
      updatePoints,
      updateTaxesAndAvailability,
      updateCurrency,
      updateAmenities,
      updateActivities,
      updateBrands,
      updateTransportationTypes,
      updateQueryAndOtherSearchQueryParam,
    };
  } else {
    // Return React Context data (only has searchQueryState and setSearchQuery)
    return contextValue;
  }
};

/**
 * Convenience hook specifically for components that should use the ADF feature flag
 * (AvailabilityCalendar and CalendarWrapper components)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag for SearchFilterContext
 */
export const useConditionalSearchFilterContextWithAdfFlag = (): SearchFilterContextType => {
  return useConditionalSearchFilterContext('adf');
};

/**
 * Convenience hook specifically for components that should use the SERP feature flag
 * (Most components in the SearchResults ecosystem)
 *
 * This hook checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag for SearchFilterContext
 */
export const useConditionalSearchFilterContextWithSerpFlag = (): SearchFilterContextType => {
  return useConditionalSearchFilterContext('serp');
};
